import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { fetchCostCenter, fetchCostCenters } from "../api/costCenters";
import { costCenterTypeLabel } from "../lib/costCenterDisplay";
import { useDebouncedValue } from "../lib/useDebouncedValue";

type Selection = { type: string; key: string } | null;

/** Searchable entity filter: the tenant can have thousands of cost centers. */
export default function CostCenterFilter({
  value,
  onChange,
}: {
  value: Selection;
  onChange: (next: Selection) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const debouncedSearch = useDebouncedValue(search.trim(), 180);
  const options = useQuery({
    queryKey: ["cost-centers", "belege-filter", debouncedSearch],
    queryFn: () => fetchCostCenters({ q: debouncedSearch, limit: 12 }),
    enabled: open,
    staleTime: 30_000,
  });
  const selected = useQuery({
    queryKey: ["cost-centers", "belege-filter-selected", value?.type, value?.key],
    queryFn: () => fetchCostCenter(value!.key, value!.type),
    enabled: Boolean(value),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!open) return;
    function closeOnOutside(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", closeOnOutside);
    return () => document.removeEventListener("mousedown", closeOnOutside);
  }, [open]);

  const selectedLabel = selected.data?.display_label ?? selected.data?.source_display_label
    ?? (value ? value.key : "Alle Kostenstellen");

  return (
    <div ref={rootRef} className="relative w-full">
      <button type="button" aria-label={`Kostenstelle: ${selectedLabel}`}
        aria-expanded={open} aria-haspopup="dialog"
        onClick={() => setOpen(!open)}
        className={`flex h-9 w-full min-w-0 items-center justify-between gap-2 rounded-md border px-3 text-sm font-medium shadow-sm ${value
          ? "border-slate-200 bg-emerald-50 text-emerald-800 ring-1 ring-emerald-100"
          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>
        <span className="truncate">{selectedLabel}</span><span aria-hidden="true" className="text-xs text-slate-400">▾</span>
      </button>
      {open ? (
        <div role="dialog" aria-label="Kostenstelle auswählen"
          onKeyDown={event => { if (event.key === "Escape") setOpen(false); }}
          className="absolute left-0 z-30 mt-1 w-[min(28rem,calc(100vw-3rem))] rounded-md border border-slate-200 bg-white p-2 shadow-lg">
          <input autoFocus type="search" aria-label="Kostenstelle suchen" value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Kostenstelle suchen …"
            className="h-9 w-full rounded-md border border-slate-300 px-3 text-sm focus:border-emerald-600 focus:outline-none" />
          <div className="mt-1 max-h-64 overflow-y-auto">
            <button type="button" onClick={() => { onChange(null); setOpen(false); setSearch(""); }}
              className="w-full rounded px-2 py-2 text-left text-sm text-slate-700 hover:bg-slate-50">
              Alle Kostenstellen
            </button>
            {options.isFetching ? <p role="status" className="px-2 py-2 text-xs text-slate-500">Suche Kostenstellen …</p>
              : options.isError ? <p role="alert" className="px-2 py-2 text-xs text-rose-700">Kostenstellen konnten nicht geladen werden.</p>
                : options.data?.items.length === 0 ? <p className="px-2 py-2 text-xs text-slate-500">Keine Kostenstelle gefunden.</p>
                  : options.data?.items.map(item => (
                    <button key={`${item.cost_center_type}:${item.cost_center_key}`} type="button"
                      onClick={() => {
                        onChange({ type: item.cost_center_type, key: item.cost_center_key });
                        setOpen(false); setSearch("");
                      }}
                      className="flex w-full min-w-0 items-center justify-between gap-3 rounded px-2 py-2 text-left text-sm text-slate-700 hover:bg-slate-50">
                      <span className="min-w-0 truncate">{item.display_label ?? item.source_display_label ?? item.cost_center_key}</span>
                      <span className="shrink-0 text-xs text-slate-500">{costCenterTypeLabel(item.cost_center_type)}</span>
                    </button>
                  ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
